import { NgClass } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { CarouselDefaultConfig, DSComponents, DesignSystemHelperService } from '@frontend/promo-utils';
import { DsArrow } from '@frontend/ui/arrow';
import { DeviceService } from '@frontend/vanilla/core';
import { LetDirective } from '@ngrx/component';
import { Store } from '@ngrx/store';
import { Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';
import { SwiperOptions } from 'swiper/types';

import { RewardContent } from '../../../common/models/reward-content';
import { targetPromoContent } from '../../../common/store/target-promotion.selectors';
import { SlabTypes } from '../../models/award-schedule/slab-type';
import { MyProgress } from '../../models/my-progress';
import { PlayerActivityDetails } from '../../models/player-activity-details';
import { isMyProgressDisabled, playerActivityDetails } from '../../store/player-activity.selectors';
import { CriteriaSlabComponent } from '../criteria-slab/criteria-slab.component';
import { CriteriaStepsComponent } from '../criteria-steps/criteria-steps.component';

interface CriteriaCarouselViewModel {
    myProgress: MyProgress;
    isMyProgressDisabled: boolean;
    isSingleSlab: boolean;
    rewardsContent: RewardContent;
}

@Component({
    selector: 'criteria-carousel',
    templateUrl: './criteria-carousel.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [LetDirective, NgClass, CriteriaSlabComponent, CriteriaStepsComponent, DsArrow],
})
export class CriteriaCarouselComponent implements OnInit, AfterViewInit, OnDestroy {
    vm$: Observable<CriteriaCarouselViewModel> = combineLatest([
        this.store.select(playerActivityDetails),
        this.store.select(targetPromoContent),
        this.store.select(isMyProgressDisabled),
    ]).pipe(
        map(([playerActivityDetails, targetPromoContent, isMyProgressDisabled]) => {
            const playerActivityContent = playerActivityDetails.playerActivityContent;
            const playerPromoConfiguration = playerActivityDetails.playerActivityMetaData?.playerPromoConfiguration;

            if (playerPromoConfiguration !== undefined) {
                this._myProgress = playerActivityContent?.myProgress;
                const slabContentLength = this._myProgress?.slabContent?.length;

                if (slabContentLength > 0) {
                    this.setSwiperConfig(slabContentLength);
                    return {
                        myProgress: this._myProgress,
                        isMyProgressDisabled,
                        isSingleSlab: playerPromoConfiguration.criteria?.slabType?.toLowerCase() === SlabTypes.Single,
                        rewardsContent: targetPromoContent.rewardsContent,
                    };
                }
            }
            return {} as CriteriaCarouselViewModel;
        }),
    );

    @ViewChild('swiperContainer') swiperRef: ElementRef;

    desktop: boolean;
    isPopperOpen: boolean = false;
    clickedSlab: number;
    swiper: Swiper;
    config: SwiperOptions;

    _myProgress: MyProgress;

    private _breakpoints1 = {
        0: {
            slidesPerView: 1,
        },
    };
    private _breakpoints2 = {
        600: {
            slidesPerView: 2.2,
        },
    };
    private _breakpoints3 = {
        600: {
            slidesPerView: 3.2,
        },
        768: {
            slidesPerView: 4.2,
        },
    };
    arrows: boolean = true;
    dsComponents: DSComponents;

    constructor(
        private store: Store<{ playerActivityModuleData: PlayerActivityDetails }>,
        private deviceService: DeviceService,
        private carouselDefaultConfig: CarouselDefaultConfig,
        private designSystemHelperService: DesignSystemHelperService,
    ) {}

    ngOnInit() {
        this.arrows = !this.deviceService.isTouch;
        this.dsComponents = this.designSystemHelperService.getDsComponentsList();
    }

    private getBreakPoints(slabContentLength: number): {
        [width: number]: SwiperOptions;
        [ratio: string]: SwiperOptions;
    } {
        let breakpoints: {
            [width: number]: SwiperOptions;
            [ratio: string]: SwiperOptions;
        };

        if (slabContentLength < 2) {
            breakpoints = this._breakpoints1;
        } else {
            if (slabContentLength < 4) {
                breakpoints = this._breakpoints2;
            } else {
                breakpoints = this._breakpoints3;
            }
        }
        return breakpoints;
    }

    setSwiperConfig(slabContentLength: number) {
        this.config = Object.assign(
            {
                modules: [Navigation],
                centeredSlides: true,
                slidesPerView: 1.8,
                slidesPerGroup: 1,
                spaceBetween: 0,
                navigation: {
                    nextEl: '.vn-carousel > .ph-carousel-next',
                    prevEl: '.vn-carousel > .ph-carousel-previous',
                },
                breakpoints: this.getBreakPoints(slabContentLength),
            },
            this.carouselDefaultConfig.defaultConfig,
        );
    }

    ngAfterViewInit() {
        this.swiper = new Swiper(this.swiperRef?.nativeElement, this.config);
        this.moveCarouselPositionBasedOnProgress();
    }

    moveCarouselPositionBasedOnProgress() {
        if (this.swiper) {
            const slabContentLength = this._myProgress?.slabContent?.length;

            if (slabContentLength > 0) {
                this.desktop = !this.deviceService.isMobile;

                if (this.desktop && slabContentLength >= 4) {
                    this.swiper.slideTo(this._myProgress.currentSlabIndexDesktop);
                } else {
                    this.swiper.slideTo(this._myProgress.currentSlabIndexMobile);
                }
            }
        }
    }

    checkPopperState(state: boolean, slab: number) {
        this.isPopperOpen = state;
        this.clickedSlab = slab;
    }

    ngOnDestroy() {
        if (this.swiper) {
            this.swiper.destroy();
        }
    }
}
